// Static imports for demo images (like AmenitiesPage)
import React, { useState, useMemo, useEffect } from "react";
import SwimmingPoolllustrationSrc from "images/demo/swimming-pool.png";
import TerracellustrationSrc from "images/demo/terrace.png";
import GrillllustrationSrc from "images/demo/grill.png";
import GymllustrationSrc from "images/demo/gym.png";
import { useAmenities } from "hooks/useAmenities";
import tw from "twin.macro";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Pagination from "components/misc/Pagination";
import Footer from "components/footers/FourColumnWithInputForm.js";
import Header from "components/headers/HeaderLight";
import { SectionHeading } from "components/misc/Headings.js";
import { Dialog, DialogTitle } from "@headlessui/react";
import PropTypes from "prop-types";

const Container = tw.div`relative`;
const Table = tw.table`min-w-full bg-white rounded-lg shadow-md`;
const Th = tw.th`py-3 px-4 bg-primary-500 text-white font-semibold text-left`;
const Td = tw.td`py-2 px-4 border-b border-gray-200 text-gray-700`;
const Tr = tw.tr`hover:bg-gray-100 transition-colors`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-3xl sm:text-4xl lg:text-5xl text-center leading-tight`;
const Button = tw.button`ml-2 px-3 py-1 rounded bg-primary-500 text-white font-semibold hover:bg-primary-700 transition`;

// Status circle component
// 0: pendiente, 1: aprobado, 2: rechazado
const StatusCircle = ({ status }) => {
  let color = "bg-yellow-400";
  let label = "Pendiente";
  if (status === 1) {
    color = "bg-green-600";
    label = "Aprobado";
  } else if (status === 2) {
    color = "bg-red-500";
    label = "Rechazado";
  }
  return (
    <span className="flex items-center gap-2">
      <span className={`inline-block w-3 h-3 rounded-full ${color}`}></span>
      {label}
    </span>
  );
};

StatusCircle.propTypes = {
  status: PropTypes.number.isRequired
};

// Payment status circle
const PayCircle = ({ isPay }) => {
  const color = isPay ? "bg-green-600" : "bg-red-500";
  const label = isPay ? "Pagado" : "Pendiente";
  return (
    <span className="flex items-center gap-2">
      <span className={`inline-block w-3 h-3 rounded-full ${color}`}></span>
      {label}
    </span>
  );
};

PayCircle.propTypes = {
  isPay: PropTypes.bool.isRequired
};

// Gallery images for amenities
const AMENITY_IMAGES = [
  { label: "Alberca", value: SwimmingPoolllustrationSrc },
  { label: "Terraza", value: TerracellustrationSrc },
  { label: "Asador", value: GrillllustrationSrc },
  { label: "Gimnasio", value: GymllustrationSrc }
];

// Helper function to get the correct image source
const getImageSrc = (imagePath) => {
  if (!imagePath) return null;
  
  // If it's already a valid URL/import, return as is
  if (imagePath.startsWith('data:') || imagePath.includes('static/')) {
    return imagePath;
  }
  
  // Map string paths to actual imports
  const imageMap = {
    "images/demo/swimming-pool.png": SwimmingPoolllustrationSrc,
    "images/demo/terrace.png": TerracellustrationSrc,
    "images/demo/grill.png": GrillllustrationSrc,
    "images/demo/gym.png": GymllustrationSrc,
    // Also support the imported variables directly
    [SwimmingPoolllustrationSrc]: SwimmingPoolllustrationSrc,
    [TerracellustrationSrc]: TerracellustrationSrc,
    [GrillllustrationSrc]: GrillllustrationSrc,
    [GymllustrationSrc]: GymllustrationSrc
  };
  
  return imageMap[imagePath] || imagePath;
};

const initialAmenityForm = { name: "", image: "", description: "", rules: [], cost: 0 };


const AdminAmenitiesPage = () => {
  const {
    amenities,
    addAmenity,
    updateAmenity,
    deleteAmenity,
    reservations,
    approveReservation,
    rejectReservation,
    fetchAmenities,
    getAllReservations
  } = useAmenities();

  // Fetch data when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchAmenities();
        await getAllReservations();
      } catch (error) {
        console.error('Error loading amenities data:', error);
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Search and filter state for reservations
  const [search, setSearch] = useState("");
  const [houseSearch, setHouseSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // Add individual loading states for better UX
  const [processingReservation, setProcessingReservation] = useState(null);
  const [processingAmenity, setProcessingAmenity] = useState(null);
  // Pagination state for reservations table
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  // Filtered reservations based on search and status
  const filteredReservations = useMemo(() => {
    let data = reservations;
    if (statusFilter !== "all") {
      data = data.filter(r => String(r.status) === statusFilter);
    }
    if (houseSearch) {
      const hs = houseSearch.toLowerCase().trim();
      data = data.filter(r => (r.house ? String(r.house).toLowerCase().trim() === hs : false));
    }
    if (search) {
      const s = search.toLowerCase().trim();
      data = data.filter(r => {
        const amenity = r.amenity ? String(r.amenity).toLowerCase().trim() : "";
        const user = r.user ? String(r.user).toLowerCase().trim() : "";
        const date = r.date ? String(r.date).toLowerCase().trim() : "";
        const time = r.time ? String(r.time).toLowerCase().trim() : "";
        return (
          amenity.includes(s) ||
          user.includes(s) ||
          date.includes(s) ||
          time.includes(s)
        );
      });
    }
    return data;
  }, [reservations, search, houseSearch, statusFilter]);

  // Reset to first page when filters change
  React.useEffect(() => { setCurrentPage(1); }, [search, houseSearch, statusFilter, reservations]);

  const [amenityModalOpen, setAmenityModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [amenityForm, setAmenityForm] = useState(initialAmenityForm);
  const [selectedAmenity, setSelectedAmenity] = useState(null);

  // Amenity CRUD handlers
  const openCreateAmenityModal = () => {
    setAmenityForm(initialAmenityForm);
    setEditMode(false);
    setAmenityModalOpen(true);
    setSelectedAmenity(null);
  };
  const openEditAmenityModal = (amenity) => {
    let rulesArray;
    if (Array.isArray(amenity.rules)) {
      rulesArray = amenity.rules;
    } else if (amenity.rules) {
      rulesArray = amenity.rules.split('\n');
    } else {
      rulesArray = [];
    }
    setAmenityForm({
      name: amenity.name || "",
      image: amenity.image || "",
      description: amenity.description || "",
      rules: rulesArray,
      cost: amenity.cost || 0
    });
    setEditMode(true);
    setAmenityModalOpen(true);
    setSelectedAmenity(amenity);
  };
  const closeAmenityModal = () => {
    setAmenityModalOpen(false);
    setTimeout(() => {
      setAmenityForm(initialAmenityForm);
      setSelectedAmenity(null);
    }, 200);
  };
  const handleAmenityChange = (e) => {
    if (e.target.name === "rules") {
      setAmenityForm({ ...amenityForm, rules: e.target.value.split('\n') });
    } else if (e.target.name === "cost") {
      setAmenityForm({ ...amenityForm, cost: Number(e.target.value) });
    } else {
      setAmenityForm({ ...amenityForm, [e.target.name]: e.target.value });
    }
  };
  const handleAmenitySubmit = async (e) => {
    e.preventDefault();
    setProcessingAmenity('save');
    try {
      let rulesArray;
      if (Array.isArray(amenityForm.rules)) {
        rulesArray = amenityForm.rules;
      } else if (amenityForm.rules) {
        rulesArray = amenityForm.rules.split('\n');
      } else {
        rulesArray = [];
      }
      const amenityToSave = { ...amenityForm, rules: rulesArray };
      
      if (editMode) {
        await updateAmenity({ ...selectedAmenity, ...amenityToSave });
      } else {
        await addAmenity(amenityToSave);
      }
      
      // Only close modal after successful operation
      closeAmenityModal();
    } catch (error) {
      console.error('Error saving amenity:', error);
      // Don't close modal on error so user can try again
    } finally {
      setProcessingAmenity(null);
    }
  };
  
  const handleAmenityDelete = async (id) => {
    if (window.confirm("¿Seguro que deseas eliminar esta amenidad?")) {
      setProcessingAmenity(id);
      try {
        await deleteAmenity(id);
      } catch (error) {
        console.error('Error deleting amenity:', error);
      } finally {
        setProcessingAmenity(null);
      }
    }
  };
  
  // Reservation approval handlers
  const handleApprove = async (id) => {
    setProcessingReservation(`approve-${id}`);
    try {
      await approveReservation(id);
    } catch (error) {
      console.error('Error approving reservation:', error);
    } finally {
      setProcessingReservation(null);
    }
  };
  
  const handleReject = async (id) => {
    setProcessingReservation(`reject-${id}`);
    try {
      await rejectReservation(id);
    } catch (error) {
      console.error('Error rejecting reservation:', error);
    } finally {
      setProcessingReservation(null);
    }
  };

  return (
    <AnimationRevealPage>
      <Header />
      <Container className="py-12">
        <Heading>Administración de Amenidades</Heading>
        {/* Reservations Approval Table */}
        <h2 className="text-xl font-bold mt-8 mb-4">Reservas de Amenidades</h2>
        {/* Search and filter bar */}
        <div className="flex flex-wrap gap-4 my-6 justify-center">
          <input
            type="text"
            placeholder="Buscar reserva..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="px-4 py-2 border rounded shadow-sm"
          />
          <input
            type="text"
            placeholder="Buscar por casa..."
            value={houseSearch}
            onChange={e => setHouseSearch(e.target.value)}
            className="px-4 py-2 border rounded shadow-sm"
          />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded shadow-sm"
          >
            <option value="all">Todos los estatus</option>
            <option value="0">Pendiente</option>
            <option value="1">Aprobado</option>
            <option value="2">Rechazado</option>
          </select>
        </div>
        <div className="overflow-x-auto mb-12 bg-white/90 backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl">
          <Table>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>Amenidad</Th>
                <Th>Usuario</Th>
                <Th>Casa</Th>
                <Th>Fecha</Th>
                <Th>Horario</Th>
                <Th>¿Pago Completado?</Th>
                <Th>Estatus</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Always return an array of rows
                if (filteredReservations.length === 0) {
                  return [
                    <tr key="no-reservations">
                      <Td colSpan={9}>No hay reservas registradas.</Td>
                    </tr>,
                    <tr key="pagination-controls">
                      <Td colSpan={9}>
                        <Pagination
                          currentPage={currentPage}
                          totalPages={1}
                          onPageChange={setCurrentPage}
                        />
                      </Td>
                    </tr>
                  ];
                } else {
                  // Pagination logic
                  const total = filteredReservations.length;
                  const totalPages = Math.ceil(total / pageSize);
                  const startIdx = (currentPage - 1) * pageSize;
                  const endIdx = startIdx + pageSize;
                  const pageData = filteredReservations.slice(startIdx, endIdx);
                  return [
                    ...pageData.map(r => (
                      <Tr key={r.id}>
                        <Td>{r.id}</Td>
                        <Td>{r.amenity}</Td>
                        <Td>{r.user}</Td>
                        <Td>{r.house}</Td>
                        <Td>{r.date}</Td>
                        <Td>{r.time}</Td>
                        <Td><PayCircle isPay={r.isPay} /></Td>
                        <Td>
                          <StatusCircle status={r.status} />
                        </Td>
                        <Td>
                          <Button
                            onClick={() => handleApprove(r.id)}
                            disabled={r.status === 1 || processingReservation === `approve-${r.id}`}
                            className={r.status === 1 ? "opacity-50 cursor-not-allowed bg-gray-400" : ""}
                          >
                            {processingReservation === `approve-${r.id}` ? "..." : "Aprobar"}
                          </Button>
                          <Button
                            onClick={() => handleReject(r.id)}
                            disabled={r.status === 2 || processingReservation === `reject-${r.id}`}
                            className={r.status === 2 ? "opacity-50 cursor-not-allowed bg-gray-400" : ""}
                          >
                            {processingReservation === `reject-${r.id}` ? "..." : "Rechazar"}
                          </Button>
                        </Td>
                      </Tr>
                    )),
                    // Pagination controls row
                    <tr key="pagination-controls">
                      <Td colSpan={9}>
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={setCurrentPage}
                        />
                      </Td>
                    </tr>
                  ];
                }
              })()}
            </tbody>
          </Table>
        </div>
        {/* Amenities CRUD Table */}
        <h2 className="text-xl font-bold mt-8 mb-4">Amenidades Activas</h2>
        <div className="flex flex-wrap gap-4 my-6 justify-center">
          <Button onClick={openCreateAmenityModal}>Agregar Amenidad</Button>
        </div>
        <div className="overflow-x-auto mb-12 bg-white/90 backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl">
          <Table>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>Nombre</Th>
                <Th>Imagen</Th>
                <Th>Descripción</Th>
                <Th>Reglas</Th>
                <Th>Costo Total</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {amenities.length === 0 ? (
                <tr><Td colSpan={6}>No hay amenidades registradas.</Td></tr>
              ) : (
                amenities.map(a => (
                  <Tr key={a.id}>
                    <Td>{a.id}</Td>
                    <Td>{a.name}</Td>
                    <Td>
                      <img src={getImageSrc(a.image)} alt={a.name} className="w-16 h-16 object-contain" />
                    </Td>
                    <Td>{a.description}</Td>
                    <Td>
                      {Array.isArray(a.rules) ? (
                        <ul className="list-disc pl-4">
                          {a.rules.map((rule, idx) => rule && <li key={rule + '-' + idx}>{rule}</li>)}
                        </ul>
                      ) : a.rules}
                    </Td>
                    <Td>${Number(a.cost || 0).toLocaleString()}</Td>
                    <Td>
                      <Button 
                        onClick={() => openEditAmenityModal(a)}
                        disabled={processingAmenity === a.id}
                      >
                        Editar
                      </Button>
                      <Button 
                        onClick={() => handleAmenityDelete(a.id)} 
                        className="bg-red-500 hover:bg-red-700 ml-2"
                        disabled={processingAmenity === a.id}
                      >
                        {processingAmenity === a.id ? "..." : "Eliminar"}
                      </Button>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
        {/* Amenity Modal */}
        {amenityModalOpen && (
          <Dialog open={amenityModalOpen} onClose={closeAmenityModal} as="div" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
            <div className="bg-white rounded-lg p-8 shadow-lg w-full max-w-md z-50 relative">
              <DialogTitle className="text-xl font-bold mb-4">{editMode ? "Editar Amenidad" : "Agregar Amenidad"}</DialogTitle>
              <form onSubmit={handleAmenitySubmit}>
                <div className="mb-4">
                  <label htmlFor="amenity-name" className="block mb-1 font-semibold">Nombre</label>
                  <input id="amenity-name" name="name" value={amenityForm.name} onChange={handleAmenityChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-4">
                  <label htmlFor="amenity-image" className="block mb-1 font-semibold">Imagen</label>
                  <select
                    id="amenity-image"
                    name="image"
                    value={amenityForm.image}
                    onChange={handleAmenityChange}
                    required
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="" disabled>Selecciona una imagen</option>
                    {AMENITY_IMAGES.map(img => (
                      <option key={img.value} value={img.value}>{img.label}</option>
                    ))}
                  </select>
                  {amenityForm.image && (
                    <div className="mt-2 flex justify-center">
                      <img src={getImageSrc(amenityForm.image)} alt="Preview" className="w-24 h-24 object-contain border rounded" />
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <label htmlFor="amenity-description" className="block mb-1 font-semibold">Descripción</label>
                  <input id="amenity-description" name="description" value={amenityForm.description} onChange={handleAmenityChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-6">
                  <label htmlFor="amenity-rules" className="block mb-1 font-semibold">Reglas (una por línea)</label>
                  <textarea
                    id="amenity-rules"
                    name="rules"
                    value={Array.isArray(amenityForm.rules) ? amenityForm.rules.join('\n') : amenityForm.rules}
                    onChange={handleAmenityChange}
                    required
                    className="w-full px-3 py-2 border rounded"
                    rows={4}
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="amenity-cost" className="block mb-1 font-semibold">Costo ($)</label>
                  <input
                    id="amenity-cost"
                    name="cost"
                    type="number"
                    min="0"
                    value={amenityForm.cost}
                    onChange={handleAmenityChange}
                    required
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" onClick={closeAmenityModal} className="bg-gray-400 hover:bg-gray-600">Cancelar</Button>
                  {(() => {
                    let buttonText;
                    if (processingAmenity === 'save') {
                      buttonText = "...";
                    } else {
                      buttonText = editMode ? "Guardar Cambios" : "Agregar";
                    }
                    return (
                      <Button 
                        type="submit" 
                        disabled={processingAmenity === 'save'}
                      >
                        {buttonText}
                      </Button>
                    );
                  })()}
                </div>
              </form>
            </div>
          </Dialog>
        )}
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};

export default AdminAmenitiesPage;
